import { useRef } from "react"
import { useQuery } from "@tanstack/react-query"
import { Redirect, useLocation } from "wouter"
import { paths } from "../paths"
import { fetchAuthSession } from "../modules/auth/api"
import { isUnauthorized } from "@rawa/api-client/ApiResponseError"
import { SadPage } from "./SadPage"
import { SERVER_UNREACHABLE_MESSAGE } from "./serverUnreachable"

// Guards a route on a staff SSO session. `loginPath` exists because not every
// guarded route belongs to the admin portal: a kiosk route sends its staff member
// to the kiosk's own sign-in screen rather than an admin-styled page on a tablet.
//
// Only a 401 means the session is the problem. A guard that redirects to sign-in
// because it could not reach the server builds a loop: sign in, land back here,
// bounce out again on the next failure (#327). So that case reports and renders
// instead.
//
// `unreachable` is the surface's own presentation of that case. It defaults to a
// full page because the client surface has no chrome of its own; a surface that
// does passes something that sits inside it.
export function ProtectedRoute({
  children,
  loginPath = paths.login,
  unreachable = <SadPage lines={[SERVER_UNREACHABLE_MESSAGE]} />,
}: {
  children: React.ReactNode
  loginPath?: string
  unreachable?: React.ReactNode
}) {
  const [location] = useLocation()
  // Retries, their delays and the reporting of an exhausted run are the query
  // client's (ADR-031). Nothing is cached, so every mount asks again.
  const { status, error, fetchStatus } = useQuery({
    queryKey: ["auth-session"],
    // The check resolves with nothing; a query has to resolve with something.
    queryFn: async () => {
      await fetchAuthSession()
      return true as const
    },
    meta: { source: "ProtectedRoute" },
  })

  // Nothing renders until this mount has an answer of its own. `gcTime: 0`
  // alone does not cover that: collection is scheduled rather than immediate,
  // so a screen re-entered straight away still finds the old answer and would
  // paint the guarded content from it before learning the credential had been
  // withdrawn.
  //
  // Once verified, a re-check runs underneath what is already on screen. The
  // window regaining focus re-checks the session, and blanking on that tore
  // down whatever the page was holding — a search term, a half-typed form —
  // every time the tab was left and returned to. A withdrawn credential still
  // redirects the moment the answer arrives.
  const verified = useRef(false)
  if (status === "success" && fetchStatus === "idle") verified.current = true

  if (!verified.current && (status === "pending" || fetchStatus === "fetching"))
    return null
  if (status === "error") {
    if (!isUnauthorized(error)) return <>{unreachable}</>
    return (
      <Redirect to={`${loginPath}?redirect=${encodeURIComponent(location)}`} />
    )
  }
  return <>{children}</>
}
